import { useState, useEffect, useRef, useCallback } from "react";
import useTranslationSetup from "../utils/useTranslationSetup";
import {
  SCREENS,
  shikshakInstructionContent,
} from "../utils/CommonUtils/constants";
import VopaInstructionScreen from "../screens/VopaInstructionScreen/VopaInstructionScreen";
import CohortMainScreen from "../screens/CohortMainScreen/CohortMainScreen";
import ComparativeAssessmentScreen from "../screens/ComparativeAssessmentScreen/ComparativeAssessmentScreen";
import useParamValues from "../utils/useParamValues";
import fetchStudentsListBySchoolId from "../fetchApi/fetchStudentsListByTeacherId";
import fetchStudentComparison from "../fetchApi/fetchStudentComparison";
import LoadingWrapper from "../components/LoadingWrapper/LoadingWrapper";
import CustomErrorDialogBox from "../components/CustomErrorDialogBox/CustomErrorDialogBox";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutline";
import * as Sentry from "@sentry/react";
import { UrlConstants } from "../utils/CommonUtils/constants";
import { captureNullCohort } from "../utils/helper";
const CohortFlow = () => {
  const { t } = useTranslationSetup();
  const [activeScreen, setActiveScreen] = useState(
    SCREENS.RENDER_INSTRUCTIONS_SCREEN,
  );
  const abortController = useRef(null);
  const comparisonAbortController = useRef(null);
  const [students, setStudents] = useState([]);
  const [comparisonStudents, setComparisonStudents] = useState(null);
  const [isComparisonLoading, setIsComparisonLoading] = useState(false);
  const [comparisonErrorOpen, setComparisonErrorOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const { token, studentIds, classNum, subjectId } = useParamValues();
  const studentListUrl = `${UrlConstants.BASE_URL}${UrlConstants.BASE_PATH_URL}student?student_ids=${studentIds}&grade=${classNum}&subject_id=${subjectId}&include_previous_results=${UrlConstants.SHOW_COMPARISON}`;
  const comparisonUrl = `${UrlConstants.BASE_URL}${UrlConstants.BASE_PATH_URL}student/comparison?student_ids=${studentIds}&grade=${classNum}&subject_id=${subjectId}`;

  const handleFetchStudents = async () => {
    try {
      const response = await fetchStudentsListBySchoolId(
        studentListUrl,
        abortController,
        token,
      );
      if (response.status === "ERROR") {
        setErrorDialogOpen(true);
      } else {
        const students = response.students || [];
        const studentsWithNullCohort = students.filter(
          (student) => student.cohort == null,
        );
        if (studentsWithNullCohort.length > 0) {
          captureNullCohort({
            source: "student-list",
            classNum,
            subjectId,
            count: studentsWithNullCohort.length,
            studentIds: studentsWithNullCohort.map(
              (student) => student.student_id,
            ),
          });
        }
        setStudents(students.filter((student) => student.cohort != null));
        setActiveScreen(SCREENS.RENDER_COHORT_VIEW_SCREEN);
      }
    } catch (err) {
      setErrorDialogOpen(true);
      Sentry.captureException(err);
    }
  };

  const handleRetryClick = () => {
    setErrorDialogOpen(false);
    setActiveScreen(SCREENS.RENDER_COHORT_LOADING_SCREEN);
    handleFetchStudents();
  };

  const fetchComparisonThenNavigate = useCallback(async () => {
    if (!UrlConstants.SHOW_COMPARISON || students.length === 0) {
      return;
    }
    setIsComparisonLoading(true);
    setComparisonErrorOpen(false);
    try {
      const payload = await fetchStudentComparison(
        comparisonUrl,
        comparisonAbortController,
        token,
      );
      const list = Array.isArray(payload) ? payload : (payload?.students ?? []);
      setComparisonStudents(list);
      setActiveScreen(SCREENS.RENDER_COHORT_COMPARATIVE_ASSESSMENT_SCREEN);
    } catch (err) {
      Sentry.captureException(err);
      setComparisonErrorOpen(true);
    } finally {
      setIsComparisonLoading(false);
    }
  }, [students, comparisonUrl, token]);

  const handleComparisonRetry = () => {
    setComparisonErrorOpen(false);
    fetchComparisonThenNavigate();
  };

  const handleInstructionsComplete = () => {
    setActiveScreen(SCREENS.RENDER_COHORT_LOADING_SCREEN);
    handleFetchStudents();
  };

  const renderActiveScreen = () => {
    switch (activeScreen) {
      case SCREENS.RENDER_INSTRUCTIONS_SCREEN:
        return (
          <VopaInstructionScreen
            instructionContent={shikshakInstructionContent(t, "cohort")}
            setActiveScreen={() => {
              sessionStorage.setItem(
                "activeScreen",
                SCREENS.RENDER_COHORT_VIEW_SCREEN,
              );
              handleInstructionsComplete();
            }}
          />
        );

      case SCREENS.RENDER_COHORT_LOADING_SCREEN:
        return (
          <>
            <LoadingWrapper type="isLoading" />
            <CustomErrorDialogBox
              header={t("something_went_wrong")}
              icon={
                <ErrorOutlineOutlinedIcon
                  style={{
                    color: "#D95959",
                    borderRadius: "10px",
                    marginTop: "16px",
                    width: "80px",
                    height: "80px",
                  }}
                />
              }
              primaryButtonText={t("try_again")}
              isDialogOpen={errorDialogOpen}
              showPrimaryButton
              onClickPrimaryButton={handleRetryClick}
            />
          </>
        );

      case SCREENS.RENDER_COHORT_VIEW_SCREEN:
        return (
          <>
            <CohortMainScreen
              students={students}
              setActiveScreen={setActiveScreen}
              onComparativeAssessmentClick={fetchComparisonThenNavigate}
              isComparisonLoading={isComparisonLoading}
            />
            <CustomErrorDialogBox
              header={t("something_went_wrong")}
              icon={
                <ErrorOutlineOutlinedIcon
                  style={{
                    color: "#D95959",
                    borderRadius: "10px",
                    marginTop: "16px",
                    width: "80px",
                    height: "80px",
                  }}
                />
              }
              primaryButtonText={t("try_again")}
              isDialogOpen={comparisonErrorOpen}
              showPrimaryButton
              onClickPrimaryButton={handleComparisonRetry}
            />
          </>
        );

      case SCREENS.RENDER_COHORT_COMPARATIVE_ASSESSMENT_SCREEN:
        return (
          <ComparativeAssessmentScreen
            totalStudents={
              new Set(
                studentIds
                  .split(",")
                  .map((id) => id.trim())
                  .filter(Boolean),
              ).size
            }
            comparisonStudents={comparisonStudents ?? []}
            setActiveScreen={setActiveScreen}
          />
        );

      default:
        return null;
    }
  };

  useEffect(() => {
    if (
      sessionStorage.getItem("activeScreen") ===
      SCREENS.RENDER_COHORT_VIEW_SCREEN
    ) {
      handleInstructionsComplete();
    }
  }, []);

  if (isComparisonLoading) {
    return <LoadingWrapper type="isLoading" />;
  }

  return <>{renderActiveScreen()}</>;
};

export default CohortFlow;
